import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Eye,
  Filter,
  Search,
  Calendar,
  MapPin,
  Camera,
  Star,
  RefreshCw,
  Plus
} from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../context/AuthContext';
import './Reports.css';
import apiClient from '../../services/api';

const Reports = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);

  const statusOptions = [
    { value: 'all', label: 'All Reports', icon: <FileText size={16} /> },
    { value: 'pending', label: 'Pending', icon: <Clock size={16} /> },
    { value: 'acknowledged', label: 'Acknowledged', icon: <Eye size={16} /> },
    { value: 'in_progress', label: 'In Progress', icon: <RefreshCw size={16} /> },
    { value: 'resolved', label: 'Resolved', icon: <CheckCircle size={16} /> },
    { value: 'rejected', label: 'Rejected', icon: <AlertCircle size={16} /> }
  ];

  const issueTypeLabels = {
    overflow: 'Bin Overflow',
    damaged: 'Damaged Bin',
    missing: 'Missing Bin',
    blocked_access: 'Blocked Access',
    foul_smell: 'Foul Smell',
    pest_infestation: 'Pest Infestation',
    improper_waste: 'Improper Waste',
    maintenance_needed: 'Maintenance Needed',
    other: 'Other Issue'
  };

  const priorityColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
    urgent: '#8b5cf6'
  };

  useEffect(() => {
    fetchReports();
  }, [filter, currentPage]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10
      });
      
      if (filter !== 'all') {
        params.append('status', filter);
      }

      const response = await apiClient.get(`/reports/user/${user.id}?${params}`);
      setReports(response.data.reports);
      setTotalReports(response.data.totalReports);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports');
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock size={16} className="status-icon pending" />,
      acknowledged: <Eye size={16} className="status-icon acknowledged" />,
      in_progress: <RefreshCw size={16} className="status-icon in-progress" />,
      resolved: <CheckCircle size={16} className="status-icon resolved" />,
      rejected: <AlertCircle size={16} className="status-icon rejected" />
    };
    return icons[status] || <FileText size={16} />;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      acknowledged: '#3b82f6',
      in_progress: '#8b5cf6',
      resolved: '#10b981',
      rejected: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Less than an hour ago';
    }
  };

  const filteredReports = reports.filter(report =>
    report.bin?.binId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issueTypeLabels[report.issueType]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && reports.length === 0) {
    return (
      <Layout title="My Reports">
        <div className="reports-container">
          <div className="loading-section">
            <div className="loader"></div>
            <p>Loading your reports...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="My Reports">
      <div className="reports-container">
        {/* Header */}
        <div className="reports-header">
          <div className="header-content">
            <h1>My Reports</h1>
            <p>Track the status of your submitted bin reports</p>
          </div>
          <Link to="/scanner" className="new-report-btn">
            <Plus size={16} />
            New Report
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon total">
              <FileText size={20} />
            </div>
            <div className="stat-content">
              <h3>{totalReports}</h3>
              <p>Total Reports</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon pending">
              <Clock size={20} />
            </div>
            <div className="stat-content">
              <h3>{reports.filter(r => r.status === 'pending').length}</h3>
              <p>Pending</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon resolved">
              <CheckCircle size={20} />
            </div>
            <div className="stat-content">
              <h3>{reports.filter(r => r.status === 'resolved').length}</h3>
              <p>Resolved</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon points">
              <Star size={20} />
            </div>
            <div className="stat-content">
              <h3>{totalReports * 10}</h3>
              <p>Points Earned</p>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="status-filters">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setFilter(option.value);
                  setCurrentPage(1);
                }}
                className={`filter-btn ${filter === option.value ? 'active' : ''}`}
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>
          
          <div className="search-section">
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search reports by bin ID, description, or issue type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        {/* Reports List */}
        {error ? (
          <div className="error-section">
            <AlertCircle size={48} className="error-icon" />
            <h2>Unable to Load Reports</h2>
            <p>{error}</p>
            <button onClick={fetchReports} className="retry-btn">
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="empty-section">
            <FileText size={48} className="empty-icon" />
            <h2>No Reports Found</h2>
            <p>
              {searchTerm 
                ? `No reports match your search "${searchTerm}"`
                : filter === 'all' 
                  ? "You haven't submitted any reports yet"
                  : `No ${filter} reports found`
              }
            </p>
            <Link to="/scanner" className="empty-action-btn">
              <Plus size={16} />
              Submit Your First Report
            </Link>
          </div>
        ) : (
          <div className="reports-list">
            {filteredReports.map((report) => (
              <div key={report._id} className="report-card">
                <div className="report-header">
                  <div className="report-id">#{report.reportId}</div>
                  <div 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(report.status) }}
                  >
                    {getStatusIcon(report.status)}
                    {report.status.replace('_', ' ')}
                  </div>
                </div>

                <div className="report-content">
                  <div className="report-main">
                    <h3 className="issue-type">
                      {issueTypeLabels[report.issueType] || report.issueType}
                    </h3>
                    <p className="description">{report.description}</p>
                    
                    <div className="report-details">
                      <div className="detail-item">
                        <MapPin size={14} />
                        <span>Bin {report.bin?.binId || report.binId}</span>
                      </div>
                      <div className="detail-item">
                        <Calendar size={14} />
                        <span>{formatDate(report.createdAt)}</span>
                      </div>
                      <div className="detail-item">
                        <div 
                          className="priority-indicator"
                          style={{ backgroundColor: priorityColors[report.priority] }}
                        ></div>
                        <span>{report.priority} priority</span>
                      </div>
                      {report.photos && report.photos.length > 0 && (
                        <div className="detail-item">
                          <Camera size={14} />
                          <span>{report.photos.length} photo{report.photos.length > 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="report-actions">
                    <Link 
                      to={`/reports/${report._id}`} 
                      className="view-details-btn"
                    >
                      <Eye size={14} />
                      View Details
                    </Link>
                  </div>
                </div>

                {/* Progress Indicator */}
                <div className="progress-indicator">
                  <div className="progress-steps">
                    <div className={`step ${['pending', 'acknowledged', 'in_progress', 'resolved'].includes(report.status) ? 'completed' : ''}`}>
                      <div className="step-dot"></div>
                      <span>Submitted</span>
                    </div>
                    <div className={`step ${['acknowledged', 'in_progress', 'resolved'].includes(report.status) ? 'completed' : ''}`}>
                      <div className="step-dot"></div>
                      <span>Acknowledged</span>
                    </div>
                    <div className={`step ${['in_progress', 'resolved'].includes(report.status) ? 'completed' : ''}`}>
                      <div className="step-dot"></div>
                      <span>In Progress</span>
                    </div>
                    <div className={`step ${report.status === 'resolved' ? 'completed' : ''}`}>
                      <div className="step-dot"></div>
                      <span>Resolved</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
